import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';

function OrgReportIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.686 2 6 4.686 6 8c0 2.21 1.195 4.14 2.97 5.19L7 22h10l-1.97-8.81C16.805 12.14 18 10.21 18 8c0-3.314-2.686-6-6-6z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
      <line x1="9" y1="22" x2="15" y2="22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function AdminUsersReportIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.5 19c.75-3 2.6-4.5 5.5-4.5 1.7 0 3.05.52 4.05 1.55" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M17.5 12.5l3 1.2v2.45c0 2.15-1.18 3.95-3 4.85-1.82-.9-3-2.7-3-4.85V13.7l3-1.2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M16.25 16.3l.85.85 1.65-1.9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

type InternalCardProps = {
  title: string;
  description: string;
  details: string[];
  to: string;
  icon: React.ReactNode;
  testId: string;
};

function InternalCard({ title, description, details, to, icon, testId }: InternalCardProps) {
  const navigate = useNavigate();
  const go = () => navigate(to);
  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      go();
    }
  };

  return (
    <div
      className="card admin-overview-card"
      role="link"
      tabIndex={0}
      onClick={go}
      onKeyDown={onKeyDown}
      data-testid={testId}
    >
      <div className="admin-overview-card-header">
        <span className="admin-overview-card-icon" aria-hidden="true">{icon}</span>
        <div className="admin-overview-card-titles">
          <div className="admin-overview-card-title">{title}</div>
          <div className="muted admin-overview-card-subtitle">{description}</div>
        </div>
        <button
          type="button"
          className="admin-overview-card-arrow"
          aria-label={`Open ${title}`}
          onClick={(e) => { e.stopPropagation(); go(); }}
        >
          <img className="admin-overview-card-arrow-icon" src="/icons/arrow-right.svg" alt="" />
        </button>
      </div>
      <div className="admin-overview-card-details">
        {details.map((text) => (
          <div key={text} className="admin-overview-card-detail">{text}</div>
        ))}
      </div>
    </div>
  );
}

export default function InternalReportsPage({ currentUser }: { currentUser: any }) {
  const navigate = useNavigate();
  const hasAdminReports =
    currentUser?.isSuperUser === true ||
    currentUser?.internalRoleActions?.includes('ReadInternalReports') === true;

  return (
    <div className="stack" data-testid="page-internal-reports">
      <PageHeader
        title="Reports"
        subtitle="Internal reports and data exports"
        actions={
                  <button
                    className="button"
                    type="button"
                    onClick={() => navigate('/internal')}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Back
                  </button>
                }
      />

      {hasAdminReports ? (
        <div className="grid two-col admin-overview-grid">
          <InternalCard
            title="Scan Regulatory Report"
            description="Scan a QR to view its stored package, product, licensee, facility and dates"
            details={['QR Lookup', 'Package and Product', 'Licensee and Location', 'Package and Print Dates']}
            to="/internal/reports/scan-regulatory"
            icon={<OrgReportIcon />}
            testId="internal-card-scan-regulatory-report"
          />
          <InternalCard
            title="Organization Facilities and Emails Report"
            description="View organization facilities along with associated email addresses"
            details={['Facilities', 'Email Addresses', 'Organization Details']}
            to="/internal/reports/facilities-and-emails-report"
            icon={<OrgReportIcon />}
            testId="internal-card-org-facilities-emails"
          />
          <InternalCard
            title="Admin Users Report"
            description="View license admin users by market and organization"
            details={['Licenses', 'Markets', 'Organizations', 'Admin Users']}
            to="/internal/reports/admin-users-report"
            icon={<AdminUsersReportIcon />}
            testId="internal-card-admin-users-report"
          />
        </div>
      ) : (
        <div className="card" style={{ padding: '2rem' }}>
          <p className="muted">You do not have permission to view reports.</p>
        </div>
      )}
    </div>
  );
}
